// Pestanya "Baixes" de Gestió d'un Empleat: llista/taula (no calendari, tal
// com demana CURRENT_TASK.md punt 8: "no mostrar calendari massiu si una
// taula/resum és més clara"), amb Baixa actual destacada, històric, creació,
// finalització, modificació i esborrat.
//
// Mateix criteri que AbsenciesEmpleat.js: es carrega tot (el volum de Baixes
// per Empleat és petit) i els filtres s'apliquen en memòria, sense tornar a
// demanar dades al servidor a cada canvi de filtre.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate } from "../../utils/Utilitats";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";
import "./baixesempleat.css";

const ESTAT_OPCIONS = [
  { valor: "totes", nom: "Totes" },
  { valor: "obertes", nom: "Obertes" },
  { valor: "tancades", nom: "Tancades" },
];

function diesTranscorreguts(dataInici, dataFinal) {
  const inici = new Date(`${dataInici}T00:00:00`);
  const final = dataFinal ? new Date(`${dataFinal}T00:00:00`) : new Date();
  const diffMs = final.setHours(0, 0, 0, 0) - inici.setHours(0, 0, 0, 0);
  return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
}

function BaixesEmpleat({ idEmpleat, idEmpresa }) {
  const [baixes, setBaixes] = useState([]);
  const [tipusOptions, setTipusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filtreTipus, setFiltreTipus] = useState("");
  const [filtreEstat, setFiltreEstat] = useState("totes");
  const [filtreDesDe, setFiltreDesDe] = useState("");
  const [filtreFinsA, setFiltreFinsA] = useState("");

  const [formCrear, setFormCrear] = useState(null);
  const [enviantCrear, setEnviantCrear] = useState(false);
  const [errorCrear, setErrorCrear] = useState("");

  const [edicio, setEdicio] = useState(null);
  const [formEdicio, setFormEdicio] = useState(null);
  const [enviantEdicio, setEnviantEdicio] = useState(false);
  const [errorEdicio, setErrorEdicio] = useState("");

  const carregarTipus = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/TipusAbsencia.php?idEmpresa=${idEmpresa}`);
      const data = await res.json();
      if (data.success) setTipusOptions(data.tipus || []);
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) console.error("Error carregant Tipus d'absència:", e);
    }
  }, [idEmpresa]);

  const carregarBaixes = useCallback(async () => {
    if (!idEmpleat || !idEmpresa) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ idEmpleat, idEmpresa });
      const res = await fetchAutenticat(`${API_URL}/BaixesEmpleat.php?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setBaixes(data.baixes || []);
      } else {
        setError(data.message || "No s'han pogut carregar les Baixes.");
        setBaixes([]);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpleat, idEmpresa]);

  useEffect(() => {
    carregarTipus();
  }, [carregarTipus]);

  useEffect(() => {
    carregarBaixes();
  }, [carregarBaixes]);

  const tipusValidsCreacio = useMemo(
    () => tipusOptions.filter((t) => Number(t.Actiu) === 1 && Number(t.EsBaixaLaboral) === 1),
    [tipusOptions]
  );

  const baixaActual = useMemo(() => baixes.find((b) => !b.DataFinal) || null, [baixes]);

  const baixesFiltrades = useMemo(() => {
    return baixes.filter((b) => {
      if (filtreTipus && String(b.TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats) !== String(filtreTipus)) return false;
      if (filtreEstat === "obertes" && b.DataFinal) return false;
      if (filtreEstat === "tancades" && !b.DataFinal) return false;
      if (filtreDesDe && (b.DataFinal || "9999-12-31") < filtreDesDe) return false;
      if (filtreFinsA && b.DataInici > filtreFinsA) return false;
      return true;
    });
  }, [baixes, filtreTipus, filtreEstat, filtreDesDe, filtreFinsA]);

  const tancarPanells = useCallback(() => {
    setFormCrear(null);
    setErrorCrear("");
    setEdicio(null);
    setFormEdicio(null);
    setErrorEdicio("");
  }, []);

  TeclaEscEnter(tancarPanells, null);

  const obrirFormCrear = () => {
    setEdicio(null);
    setFormEdicio(null);
    setFormCrear({
      idTipus: tipusValidsCreacio[0]?.idTipusAbsenciaEmpleats || "",
      dataInici: "",
      dataFinal: "",
      notes: "",
    });
    setErrorCrear("");
  };

  const obrirFormEdicio = (baixa) => {
    setFormCrear(null);
    setEdicio(baixa);
    setFormEdicio({
      idTipus: baixa.TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats,
      dataInici: baixa.DataInici,
      dataFinal: baixa.DataFinal || "",
      notes: baixa.Notes || "",
    });
    setErrorEdicio("");
  };

  const handleCrearBaixa = async () => {
    if (!formCrear.idTipus) {
      setErrorCrear("Selecciona un Tipus de Baixa.");
      return;
    }
    if (!formCrear.dataInici) {
      setErrorCrear("Indica la Data d'inici.");
      return;
    }
    setEnviantCrear(true);
    setErrorCrear("");
    try {
      const res = await fetchAutenticat(`${API_URL}/NovaBaixaEmpleat.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idEmpleat,
          TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats: formCrear.idTipus,
          DataInici: formCrear.dataInici,
          DataFinal: formCrear.dataFinal || null,
          Notes: formCrear.notes || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        tancarPanells();
        carregarBaixes();
      } else {
        setErrorCrear(data.message || "No s'ha pogut crear la Baixa.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorCrear("Error de connexió amb el servidor.");
    } finally {
      setEnviantCrear(false);
    }
  };

  const handleModificarBaixa = async () => {
    if (!edicio || !formEdicio) return;
    if (!formEdicio.dataInici) {
      setErrorEdicio("Indica la Data d'inici.");
      return;
    }
    setEnviantEdicio(true);
    setErrorEdicio("");
    try {
      const res = await fetchAutenticat(`${API_URL}/ModificarBaixaEmpleat.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idBaixesLaborals: edicio.idBaixesLaborals,
          TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats: formEdicio.idTipus,
          DataInici: formEdicio.dataInici,
          DataFinal: formEdicio.dataFinal || null,
          Notes: formEdicio.notes || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        tancarPanells();
        carregarBaixes();
      } else {
        setErrorEdicio(data.message || "No s'ha pogut modificar la Baixa.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorEdicio("Error de connexió amb el servidor.");
    } finally {
      setEnviantEdicio(false);
    }
  };

  const handleFinalitzarBaixa = async (baixa) => {
    const avui = new Date();
    const avuiISO = `${avui.getFullYear()}-${String(avui.getMonth() + 1).padStart(2, "0")}-${String(avui.getDate()).padStart(2, "0")}`;
    if (!window.confirm(`Finalitzar la Baixa iniciada el ${formatDate(baixa.DataInici)} amb Data final ${formatDate(avuiISO)}?`)) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/ModificarBaixaEmpleat.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idBaixesLaborals: baixa.idBaixesLaborals,
          TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats: baixa.TipusAbsenciaEmpleats_idTipusAbsenciaEmpleats,
          DataInici: baixa.DataInici,
          DataFinal: avuiISO,
          Notes: baixa.Notes || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        tancarPanells();
        carregarBaixes();
      } else {
        alert(data.message || "No s'ha pogut finalitzar la Baixa.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  const handleEsborrarBaixa = async () => {
    if (!edicio) return;
    if (!window.confirm(`Esborrar la Baixa iniciada el ${formatDate(edicio.DataInici)}?`)) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/EsborrarBaixaEmpleat.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa, idBaixesLaborals: edicio.idBaixesLaborals }),
      });
      const data = await res.json();
      if (data.success) {
        tancarPanells();
        carregarBaixes();
      } else {
        alert(data.message || "No s'ha pogut esborrar la Baixa.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  if (!idEmpleat || !idEmpresa) {
    return <p>Falten dades (Empleat o Empresa) per carregar les Baixes.</p>;
  }

  return (
    <div className="baixes-emp">
      <div className="baixes-emp-filtres">
        <label>
          Tipus:
          <select value={filtreTipus} onChange={(e) => setFiltreTipus(e.target.value)}>
            <option value="">Tots</option>
            {tipusOptions.filter((t) => Number(t.EsBaixaLaboral) === 1).map((t) => (
              <option key={t.idTipusAbsenciaEmpleats} value={t.idTipusAbsenciaEmpleats}>{t.Nom}</option>
            ))}
          </select>
        </label>
        <label>
          Estat:
          <select value={filtreEstat} onChange={(e) => setFiltreEstat(e.target.value)}>
            {ESTAT_OPCIONS.map((o) => (
              <option key={o.valor} value={o.valor}>{o.nom}</option>
            ))}
          </select>
        </label>
        <label>
          Des de:
          <input type="date" value={filtreDesDe} onChange={(e) => setFiltreDesDe(e.target.value)} />
        </label>
        <label>
          Fins a:
          <input type="date" value={filtreFinsA} onChange={(e) => setFiltreFinsA(e.target.value)} />
        </label>
      </div>

      {loading && <p>Carregant Baixes...</p>}
      {error && <p className="baixes-emp-error">{error}</p>}

      {!loading && !error && (
        <>
          {baixaActual ? (
            <div className="baixes-emp-actual">
              <div className="baixes-emp-actual-info">
                <span className="baixes-emp-actual-titol">Baixa actual (oberta)</span>
                <span
                  className="baixes-emp-tipus-chip"
                  style={{ backgroundColor: baixaActual.TipusColor || "#999999" }}
                >
                  {baixaActual.TipusNom}
                </span>
                <span>Des de: <strong>{formatDate(baixaActual.DataInici)}</strong></span>
                <span>{diesTranscorreguts(baixaActual.DataInici, null)} dies transcorreguts</span>
              </div>
              {baixaActual.Notes && <div className="baixes-emp-actual-notes">{baixaActual.Notes}</div>}
              <div className="Dashboard-button-group">
                <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={() => handleFinalitzarBaixa(baixaActual)}>
                  Finalitzar avui
                </button>
                <button type="button" className="Dashboard-btn" onClick={() => obrirFormEdicio(baixaActual)}>
                  Modificar
                </button>
              </div>
            </div>
          ) : (
            <p className="baixes-emp-sense-oberta">No hi ha cap Baixa oberta actualment.</p>
          )}

          <div className="Dashboard-button-group baixes-emp-accions">
            <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={obrirFormCrear}>
              Nova Baixa
            </button>
          </div>

          {baixesFiltrades.length > 0 ? (
            <div className="Dashboard-table-container">
              <table className="Dashboard-table">
                <thead>
                  <tr>
                    <th>Tipus</th>
                    <th>Data inici</th>
                    <th>Data final</th>
                    <th>Dies</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {baixesFiltrades.map((b) => (
                    <tr
                      key={b.idBaixesLaborals}
                      className={edicio?.idBaixesLaborals === b.idBaixesLaborals ? "selected-row" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => obrirFormEdicio(b)}
                    >
                      <td>
                        <span className="baixes-emp-tipus-chip" style={{ backgroundColor: b.TipusColor || "#999999" }}>
                          {b.TipusNom}
                        </span>
                      </td>
                      <td>{formatDate(b.DataInici)}</td>
                      <td>{b.DataFinal ? formatDate(b.DataFinal) : "Oberta"}</td>
                      <td>{diesTranscorreguts(b.DataInici, b.DataFinal)}</td>
                      <td>{b.Notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No s'ha trobat cap Baixa amb aquests filtres.</p>
          )}
        </>
      )}

      {formCrear && (
        <div className="Dashboard-form-section baixes-emp-form">
          <h4>Nova Baixa</h4>
          <div className="Dashboard-form-row">
            <label>
              Tipus:
              <select value={formCrear.idTipus} onChange={(e) => setFormCrear((p) => ({ ...p, idTipus: e.target.value }))}>
                <option value="">Selecciona un Tipus</option>
                {tipusValidsCreacio.map((t) => (
                  <option key={t.idTipusAbsenciaEmpleats} value={t.idTipusAbsenciaEmpleats}>{t.Nom}</option>
                ))}
              </select>
            </label>
            <label>
              Data inici:
              <input type="date" value={formCrear.dataInici} onChange={(e) => setFormCrear((p) => ({ ...p, dataInici: e.target.value }))} />
            </label>
            <label>
              Data final (opcional):
              <input type="date" value={formCrear.dataFinal} onChange={(e) => setFormCrear((p) => ({ ...p, dataFinal: e.target.value }))} />
            </label>
          </div>
          <div className="Dashboard-form-row">
            <label>
              Notes:
              <textarea value={formCrear.notes} onChange={(e) => setFormCrear((p) => ({ ...p, notes: e.target.value }))} maxLength="255" />
            </label>
          </div>
          {errorCrear && <p className="baixes-emp-error">{errorCrear}</p>}
          <div className="Dashboard-button-group">
            <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleCrearBaixa} disabled={enviantCrear}>
              Crear
            </button>
            <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={tancarPanells} disabled={enviantCrear}>
              Cancel·lar
            </button>
          </div>
        </div>
      )}

      {edicio && formEdicio && (
        <div className="Dashboard-form-section baixes-emp-form">
          <h4>Baixa des del {formatDate(edicio.DataInici)}</h4>
          <div className="Dashboard-form-row">
            <label>
              Tipus:
              <select value={formEdicio.idTipus} onChange={(e) => setFormEdicio((p) => ({ ...p, idTipus: e.target.value }))}>
                {tipusOptions.filter((t) => Number(t.EsBaixaLaboral) === 1 || String(t.idTipusAbsenciaEmpleats) === String(formEdicio.idTipus)).map((t) => (
                  <option key={t.idTipusAbsenciaEmpleats} value={t.idTipusAbsenciaEmpleats}>{t.Nom}</option>
                ))}
              </select>
            </label>
            <label>
              Data inici:
              <input type="date" value={formEdicio.dataInici} onChange={(e) => setFormEdicio((p) => ({ ...p, dataInici: e.target.value }))} />
            </label>
            <label>
              Data final (buit = oberta):
              <input type="date" value={formEdicio.dataFinal} onChange={(e) => setFormEdicio((p) => ({ ...p, dataFinal: e.target.value }))} />
            </label>
          </div>
          <div className="Dashboard-form-row">
            <label>
              Notes:
              <textarea value={formEdicio.notes} onChange={(e) => setFormEdicio((p) => ({ ...p, notes: e.target.value }))} maxLength="255" />
            </label>
          </div>
          {errorEdicio && <p className="baixes-emp-error">{errorEdicio}</p>}
          <div className="Dashboard-button-group">
            <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleModificarBaixa} disabled={enviantEdicio}>
              Modificar
            </button>
            <button type="button" className="Dashboard-btn baixes-emp-btn-esborrar" onClick={handleEsborrarBaixa} disabled={enviantEdicio}>
              Esborrar
            </button>
            <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={tancarPanells} disabled={enviantEdicio}>
              Tancar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BaixesEmpleat;
